import React, { useState, useEffect, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  GeoJSON,
  useMap,
  FeatureGroup,
} from 'react-leaflet';
import { EditControl } from "react-leaflet-draw";
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import axios from 'axios';
import L from 'leaflet';
import * as turf from '@turf/turf';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
  Checkbox,
  FormGroup,
  Divider,
  Alert,
} from '@mui/material';
import { notifyActiveEventsUpdated } from '../../../../../utils/activeEventsBus';
import { MapHomeControl, MapInfoControl } from "../../../../common/MapControls/MapControls";
import { useLiveLocation } from "../../../../common/useLiveLocation";
import CircularProgress from "@mui/material/CircularProgress";

window.L = L;

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Submitting a felt report now records who submitted it, so it requires a
// logged-in session - the same JWT every other authenticated call in this
// app already attaches.
const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SectionLabel = ({ text }) => (
  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
    {text}
  </Typography>
);

const FieldLabel = ({ text, required }) => (
  <FormLabel sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 0.5, display: 'block' }}>
    {text}{required && <Box component="span" sx={{ color: '#ef4444', ml: 0.5 }}>*</Box>}
  </FormLabel>
);

const createNumberedIcon = (number) =>
  L.divIcon({
    className: 'custom-number-icon',
    html: `<div class="number-circle">${number}</div>`,
    iconSize: [25, 25],
    iconAnchor: [12, 25],
    popupAnchor: [0, -25],
  });

const icon1 = createNumberedIcon(1);
const icon2 = createNumberedIcon(2);

// Polygon drawing style: black dashed outline (no fill), small plain white
// vertex dots instead of leaflet-draw's default larger blue markers.
const polygonVertexIcon = L.divIcon({
  className: 'felt-report-polygon-vertex',
  iconSize: [6, 6],
});
const polygonShapeOptions = { color: '#000000', weight: 2, dashArray: '4,4', fillOpacity: 0 };

// Pans the map to a geolocated position - only fires on "Use My Location"
// (via the `target` prop's changing `ts`), never on a plain map click/drag,
// so manually placing/adjusting the marker never yanks the view around.
const PanToLatLng = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lon], 17);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.ts]);
  return null;
};

const TwoPointMapHandler = ({ twoPoints, setTwoPoints, setFormData, drawMode, onPointMoved }) => {
  useMapEvents({
    click(e) {
      if (drawMode === 'twoPoints') {
        setTwoPoints(prev => {
          const next = [...prev, e.latlng];
          return next.length > 2 ? [e.latlng] : next;
        });
        onPointMoved?.(e.latlng.lat, e.latlng.lng);
      }
    },
  });

  useEffect(() => {
    if (twoPoints.length === 1) {
      setFormData(prev => ({
        ...prev,
        starting_point_lat: twoPoints[0].lat.toFixed(6),
        starting_point_long: twoPoints[0].lng.toFixed(6),
        ending_point_lat: '', ending_point_long: '',
        latitude: '', longitude: '', geojson_feature: null,
      }));
    } else if (twoPoints.length === 2) {
      setFormData(prev => ({
        ...prev,
        starting_point_lat: twoPoints[0].lat.toFixed(6),
        starting_point_long: twoPoints[0].lng.toFixed(6),
        ending_point_lat: twoPoints[1].lat.toFixed(6),
        ending_point_long: twoPoints[1].lng.toFixed(6),
        latitude: '', longitude: '', geojson_feature: null,
      }));
    } else {
      setFormData(prev => ({ ...prev, starting_point_lat: '', starting_point_long: '', ending_point_lat: '', ending_point_long: '' }));
    }
  }, [twoPoints, setFormData]);

  return (
    <>
      {twoPoints.length > 0 && <Marker position={twoPoints[0]} icon={icon1}><Popup>Point 1</Popup></Marker>}
      {twoPoints.length > 1 && <Marker position={twoPoints[1]} icon={icon2}><Popup>Point 2</Popup></Marker>}
    </>
  );
};

const FormMap = ({ markerPosition, setMarkerPosition, setFormData, drawMode, onPointMoved, onBufferCleared }) => {
  useMapEvents({
    click(e) {
      if (drawMode === 'singlePoint') {
        setMarkerPosition(e.latlng);
        setFormData(prev => ({
          ...prev,
          latitude: e.latlng.lat.toFixed(6),
          longitude: e.latlng.lng.toFixed(6),
          starting_point_lat: '', starting_point_long: '',
          ending_point_lat: '', ending_point_long: '',
          geojson_feature: null,
        }));
        onBufferCleared?.();
        onPointMoved?.(e.latlng.lat, e.latlng.lng);
      }
    },
  });

  const handleDragEnd = (e) => {
    const latlng = e.target.getLatLng();
    setMarkerPosition(latlng);
    setFormData(prev => ({ ...prev, latitude: latlng.lat.toFixed(6), longitude: latlng.lng.toFixed(6), geojson_feature: null }));
    onBufferCleared?.();
    onPointMoved?.(latlng.lat, latlng.lng);
  };

  if (!markerPosition) return null;

  if (drawMode === 'singlePoint') {
    return (
      <Marker position={markerPosition} draggable={true} eventHandlers={{ dragend: handleDragEnd }}>
        <Popup>
          Latitude: {markerPosition.lat.toFixed(6)}<br />Longitude: {markerPosition.lng.toFixed(6)}<br />
          <em>Drag to adjust — Municipality/Post-Admin/Suco update automatically.</em>
        </Popup>
      </Marker>
    );
  }

  // Two Points/Polygon mode: the marked/live point isn't cleared, just
  // shown masked (low opacity, same pin icon, not draggable, not part of
  // what gets submitted) so it stays visible while drawing around it
  // instead of vanishing on mode switch.
  return (
    <Marker position={markerPosition} opacity={0.45}>
      <Popup>Reference point — not submitted in this mode.</Popup>
    </Marker>
  );
};

const getCurrentDate = () => new Date().toISOString().split('T')[0];
const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
};

const initialFormData = {
  location: '',
  latitude: '', longitude: '',
  date_flood: getCurrentDate(),
  time_flood: getCurrentTime(),
  time_type: '',
  water_level: '',
  flood_duration: '',
  apparent_cause: '',
  damage_type: [],
  affected_status: '',
  additional_info: '',
  municipality: '', sub_municipality: '', suco: '',
  starting_point_lat: '', starting_point_long: '',
  ending_point_lat: '', ending_point_long: '',
  geojson_feature: null,
  people_injured_count: '',
  people_deaths_count: '',
  houses_affected_count: '',
  livestock_lost_count: '',
  estimated_loss_amount: '',
};

function FeltReport() {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [twoPoints, setTwoPoints] = useState([]);
  const [drawMode, setDrawMode] = useState('singlePoint');
  const [drawnItems, setDrawnItems] = useState(null);
  const [bufferMeters, setBufferMeters] = useState('');
  const [isBufferPolygon, setIsBufferPolygon] = useState(false);
  const featureGroupRef = useRef(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [formData, setFormData] = useState(initialFormData);
  const [municipalities, setMunicipalities] = useState([]);
  const [subMunicipalities, setSubMunicipalities] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [sucos, setSucos] = useState([]);
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState('');

  // "Use My Location" - browser geolocation + adm3-boundary reverse lookup,
  // shared with every other felt-report form. geoFlyTarget only drives the
  // map's one-off flyTo on a fresh geolocation fix, never on a manual
  // click/drag of the marker.
  const { locating, locationError, setLocationError, requestBrowserLocation, resolveAdmFromLatLng, resolveLandmark } = useLiveLocation();
  const [geoFlyTarget, setGeoFlyTarget] = useState(null);
  const [landmarkLoading, setLandmarkLoading] = useState(false);
  const [landmarkNotFound, setLandmarkNotFound] = useState(false);

  // Fills Municipality/Post-Admin/Suco from a resolved adm hierarchy -
  // shared by both the initial geolocation fix and every later manual
  // click/drag of the marker, so the two paths can't drift apart.
  const applyResolvedAdm = (adm) => {
    setSelectedMunicipality(adm.adm1_code);
    setSelectedSubMunicipality(adm.adm2_code);
    setFormData(prev => ({ ...prev, municipality: adm.adm1_code, sub_municipality: adm.adm2_code, suco: adm.adm3_code }));
  };

  const handlePointMoved = async (lat, lon) => {
    const adm = await resolveAdmFromLatLng(lat, lon);
    if (adm) { applyResolvedAdm(adm); setLocationError(""); }
    else setLocationError("This point doesn't fall within a known suco boundary - select Municipality/Post-Admin/Suco manually below.");

    setLandmarkLoading(true);
    setLandmarkNotFound(false);
    const landmark = await resolveLandmark(lat, lon);
    setLandmarkLoading(false);
    if (landmark) setFormData(prev => ({ ...prev, location: landmark }));
    else { setFormData(prev => ({ ...prev, location: '' })); setLandmarkNotFound(true); }
  };

  const handleUseMyLocation = async () => {
    try {
      const { lat, lon } = await requestBrowserLocation();
      setDrawMode('singlePoint');
      setTwoPoints([]);
      setDrawnItems(null);
      setBufferMeters(''); setIsBufferPolygon(false);
      setMarkerPosition({ lat, lng: lon });
      setGeoFlyTarget({ lat, lon, ts: Date.now() });
      setFormData(prev => ({
        ...prev,
        latitude: lat.toFixed(6), longitude: lon.toFixed(6),
        starting_point_lat: '', starting_point_long: '',
        ending_point_lat: '', ending_point_long: '',
        geojson_feature: null,
      }));
      await handlePointMoved(lat, lon);
    } catch (err) {
      // requestBrowserLocation already recorded a user-facing locationError
    }
  };

  // Prompts for location as soon as the form opens - no button to click,
  // same one-shot request handleUseMyLocation always made, just fired on
  // mount instead of a click. If denied/unsupported, locationError guides
  // the user to the manual dropdowns below, which always still work.
  useEffect(() => {
    handleUseMyLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .custom-number-icon { background-color: transparent !important; border: none !important; box-shadow: none !important; }
      .felt-report-polygon-vertex { background-color: #ffffff; border: 1px solid #000000; border-radius: 50%; }
      .number-circle {
        background-color: #2563eb; color: white; border-radius: 50%;
        width: 25px; height: 25px; display: flex; justify-content: center;
        align-items: center; font-weight: bold; border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    axios.post(`${backendUrl}Municipalities/municipalities_list/`).then(r => setMunicipalities(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedMunicipality) {
      axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`, { adm2_code: selectedMunicipality })
        .then(r => setSubMunicipalities(r.data)).catch(console.error);
    } else { setSubMunicipalities([]); setSucos([]); }
  }, [selectedMunicipality]);

  useEffect(() => {
    if (selectedSubMunicipality) {
      axios.post(`${backendUrl}Municipalities/suco_list/`, { adm2_code: selectedSubMunicipality })
        .then(r => setSucos(r.data)).catch(console.error);
    } else { setSucos([]); }
  }, [selectedSubMunicipality]);

  const handleMunicipalityChange = (e) => {
    setSelectedMunicipality(e.target.value);
    setSelectedSubMunicipality('');
    setSucos([]);
    setFormData(prev => ({ ...prev, municipality: e.target.value, sub_municipality: '', suco: '' }));
  };

  const handleSubMunicipalityChange = (e) => {
    setSelectedSubMunicipality(e.target.value);
    setSucos([]);
    setFormData(prev => ({ ...prev, sub_municipality: e.target.value, suco: '' }));
  };

  const handleSucoChange = (e) => setFormData(prev => ({ ...prev, suco: e.target.value }));

  const handleDrawModeChange = (e) => {
    setDrawMode(e.target.value);
    // markerPosition is deliberately left alone here - it stays visible as
    // a reference dot when switching away from Single Point (FormMap
    // renders it as a masked/low-opacity pin in that case) instead of vanishing.
    setTwoPoints([]); setDrawnItems(null);
    setBufferMeters(''); setIsBufferPolygon(false);
    setFormData(prev => ({ ...prev, latitude: '', longitude: '', starting_point_lat: '', starting_point_long: '', ending_point_lat: '', ending_point_long: '', geojson_feature: null }));
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'damage_type') {
      setFormData(prev => ({
        ...prev,
        damage_type: checked ? [...prev.damage_type, value] : prev.damage_type.filter(v => v !== value),
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setMarkerPosition(null); setTwoPoints([]); setDrawnItems(null);
    setBufferMeters(''); setIsBufferPolygon(false);
    setDrawMode('singlePoint');
    setSelectedMunicipality(''); setSelectedSubMunicipality(''); setSucos([]);
    setSubmitSuccess(false); setSubmitError('');
    setGeoFlyTarget(null); setLocationError(''); setLandmarkNotFound(false);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(''); setSubmitSuccess(false);
    if (!formData.additional_info || !formData.additional_info.trim()) {
      setSubmitError('Description is required.');
      return;
    }
    const dataToSend = new FormData();
    for (const key in formData) {
      if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
        if (key === 'damage_type') dataToSend.append(key, JSON.stringify(formData[key]));
        else if (key === 'geojson_feature' && formData.geojson_feature) dataToSend.append(key, JSON.stringify(formData.geojson_feature));
        else dataToSend.append(key, formData[key]);
      }
    }
    try {
      const response = await axios.post(`${backendUrl}flood/flood-felt-reports/`, dataToSend, { headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() } });
      if (response.status === 201 || response.status === 200) {
        setSubmitSuccess(true);
        notifyActiveEventsUpdated();
        setTimeout(handleReset, 2000);
      } else {
        setSubmitError('Failed to submit report. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error.response ? error.response.data : error.message);
      setSubmitError('An error occurred while submitting the report. Please check the console for details.');
    }
  };

  const onCreated = (e) => {
    const { layer } = e;
    if (drawnItems) drawnItems.clearLayers();
    // leaflet-draw adds `layer` straight into the real FeatureGroup itself
    // (not into our `drawnItems` copy above) - remove any older shapes from
    // it so a second polygon doesn't leave the first one stuck on the map.
    featureGroupRef.current?.eachLayer((l) => {
      if (l !== layer) featureGroupRef.current.removeLayer(l);
    });
    const newDrawnItems = new L.FeatureGroup();
    newDrawnItems.addLayer(layer);
    setDrawnItems(newDrawnItems);
    setIsBufferPolygon(false);
    setFormData(prev => ({ ...prev, geojson_feature: layer.toGeoJSON(), latitude: '', longitude: '', starting_point_lat: '', starting_point_long: '', ending_point_lat: '', ending_point_long: '' }));
    const center = layer.getBounds().getCenter();
    handlePointMoved(center.lat, center.lng);
  };

  const handleClearPolygon = () => {
    setDrawnItems(null);
    setIsBufferPolygon(false);
    featureGroupRef.current?.clearLayers();
    setFormData(prev => ({ ...prev, geojson_feature: null }));
  };

  // Buffer coordinates are submitted exactly like a hand-drawn polygon - the
  // radio switches to Polygon so that's reflected in the UI too, but the
  // meters field/button stay reachable there so the same button can update
  // the buffer's radius instead of only being able to add it once.
  const handleApplyBuffer = () => {
    const meters = parseFloat(bufferMeters);
    if (!markerPosition || !meters || meters <= 0) return;
    const bufferFeature = turf.buffer(
      turf.point([markerPosition.lng, markerPosition.lat]), meters, { units: 'meters' }
    );
    const layer = L.geoJSON(bufferFeature).getLayers()[0];
    const newDrawnItems = new L.FeatureGroup();
    newDrawnItems.addLayer(layer);
    setDrawnItems(newDrawnItems);
    setIsBufferPolygon(true);
    setFormData(prev => ({ ...prev, geojson_feature: layer.toGeoJSON(), latitude: '', longitude: '' }));
    setDrawMode('polygon');
  };

  const handleClearBuffer = () => {
    setDrawnItems(null);
    setIsBufferPolygon(false);
    featureGroupRef.current?.clearLayers();
    setDrawMode('singlePoint');
    setFormData(prev => ({
      ...prev, geojson_feature: null,
      latitude: markerPosition ? markerPosition.lat.toFixed(6) : prev.latitude,
      longitude: markerPosition ? markerPosition.lng.toFixed(6) : prev.longitude,
    }));
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '15px',
      '& fieldset': { borderColor: '#d1d5db' },
      '&:hover fieldset': { borderColor: '#2563eb' },
      '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontSize: '15px', '&.Mui-focused': { color: '#2563eb' } },
  };
  const radioSx = { color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };
  const checkSx = { p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>

      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Flood Felt Report</Typography>
        <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.25 }}>Report flood observations and impacts</Typography>
      </Box>

      <Box component="form" onSubmit={handleReportSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {submitSuccess && <Alert severity="success" sx={{ fontSize: '14px', borderRadius: '8px' }}>Flood Report submitted successfully! Form will reset in 2 seconds.</Alert>}
        {submitError && <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>{submitError}</Alert>}

        {/* Location: details on the left, map on the right */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: 3 }}>
          {/* LEFT: Location Information */}
          <Box>
            <SectionLabel text="Location Information" />

            {locating && (
              <Typography sx={{ fontSize: '13px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={14} /> Requesting your location…
              </Typography>
            )}

            {locationError && !(selectedMunicipality && selectedSubMunicipality && formData.suco) && <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px', mb: 2 }}>{locationError}</Alert>}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2.5 }}>
              <TextField label="Municipality" select size="small" fullWidth sx={inputSx} value={selectedMunicipality} onChange={handleMunicipalityChange}>
                <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a municipality</em></MenuItem>
                {municipalities.map((m, i) => <MenuItem key={i} value={m.adm1_code}>{m.adm1}</MenuItem>)}
              </TextField>
              <TextField label="Post-Admin" select size="small" fullWidth sx={inputSx} value={formData.sub_municipality} onChange={handleSubMunicipalityChange} disabled={!selectedMunicipality}>
                <MenuItem value=""><em style={{ color: '#94a3b8' }}>{selectedMunicipality ? 'Select a Post-Admin' : 'Select a municipality first'}</em></MenuItem>
                {subMunicipalities.map((s, i) => <MenuItem key={i} value={s.adm2_code}>{s.adm2}</MenuItem>)}
              </TextField>
              <TextField label="Suco" select size="small" fullWidth sx={inputSx} value={formData.suco} onChange={handleSucoChange} disabled={!selectedSubMunicipality}>
                <MenuItem value=""><em style={{ color: '#94a3b8' }}>{selectedSubMunicipality ? 'Select a suco' : 'Select a Post-Admin first'}</em></MenuItem>
                {sucos.map((s, i) => <MenuItem key={i} value={s.adm3_code}>{s.adm3}</MenuItem>)}
              </TextField>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <FormControl>
                <FieldLabel text="How do you want to define the location?" />
                <RadioGroup row value={drawMode} onChange={handleDrawModeChange}>
                  {[['singlePoint','Single Point'],['twoPoints','Two Points'],['polygon','Polygon']].map(([v,l]) => (
                    <FormControlLabel key={v} value={v} control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>{l}</Typography>} sx={{ mr: 2 }} />
                  ))}
                </RadioGroup>
              </FormControl>
              <TextField label="Nearest landmark/location" size="small" fullWidth sx={{ ...inputSx, mt: 2 }} name="location" value={formData.location} onChange={handleChange}
                placeholder={landmarkLoading ? 'Looking up the nearest landmark…' : undefined} />
              {landmarkNotFound && !formData.location && (
                <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 0.5 }}>No landmark found nearby — add one.</Typography>
              )}
            </Box>

            {drawMode === 'singlePoint' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
                <TextField label="Latitude" size="small" fullWidth sx={inputSx} value={formData.latitude} InputProps={{ readOnly: true }} />
                <TextField label="Longitude" size="small" fullWidth sx={inputSx} value={formData.longitude} InputProps={{ readOnly: true }} />
              </Box>
            )}
            {drawMode === 'singlePoint' && markerPosition && (
              <Box sx={{ mt: 2.5, p: 2, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <FieldLabel text="Buffer zone around this point (meters)" />
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField size="small" type="number" label="Meters" sx={{ ...inputSx, width: 120 }}
                    value={bufferMeters} onChange={(e) => setBufferMeters(e.target.value)} />
                  <Button size="small" variant="contained" onClick={handleApplyBuffer}
                    disabled={!bufferMeters || parseFloat(bufferMeters) <= 0}
                    sx={{ textTransform: 'none', fontSize: '13px', background: '#2563eb', '&:hover': { background: '#1d4ed8' } }}>
                    Add Buffer Zone
                  </Button>
                </Box>
              </Box>
            )}
            {drawMode === 'twoPoints' && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
                  <TextField label="Starting Latitude" size="small" fullWidth sx={inputSx} value={formData.starting_point_lat} InputProps={{ readOnly: true }} />
                  <TextField label="Starting Longitude" size="small" fullWidth sx={inputSx} value={formData.starting_point_long} InputProps={{ readOnly: true }} />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
                  <TextField label="Ending Latitude" size="small" fullWidth sx={inputSx} value={formData.ending_point_lat} InputProps={{ readOnly: true }} />
                  <TextField label="Ending Longitude" size="small" fullWidth sx={inputSx} value={formData.ending_point_long} InputProps={{ readOnly: true }} />
                </Box>
              </>
            )}
            {drawMode === 'polygon' && (
              <Box sx={{ mt: 2.5, p: 2, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                {isBufferPolygon ? (
                  <>
                    <FieldLabel text="Buffer zone around this point (meters)" />
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField size="small" type="number" label="Meters" sx={{ ...inputSx, width: 120 }}
                        value={bufferMeters} onChange={(e) => setBufferMeters(e.target.value)} />
                      <Button size="small" variant="contained" onClick={handleApplyBuffer}
                        disabled={!bufferMeters || parseFloat(bufferMeters) <= 0}
                        sx={{ textTransform: 'none', fontSize: '13px', background: '#2563eb', '&:hover': { background: '#1d4ed8' } }}>
                        Update Buffer Zone
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={handleClearBuffer}
                        sx={{ textTransform: 'none', fontSize: '13px' }}>
                        Clear
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography sx={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic' }}>Click the polygon icon on the map to draw the area</Typography>
                    {drawnItems && (
                      <Button size="small" variant="outlined" color="error" onClick={handleClearPolygon}
                        sx={{ mt: 1.5, textTransform: 'none', fontSize: '13px' }}>
                        Clear Polygon
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* RIGHT: Map */}
          <Box>
            <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <MapContainer center={[-8.7747, 125.7275]} zoom={8} maxZoom={22} style={{ height: '450px', width: '100%' }}>
                <MapHomeControl />
                <MapInfoControl text="This form asks for your location when it opens and auto-fills Municipality/Post-Admin/Suco. Click the map (or drag the pin) to adjust it." />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={22} maxNativeZoom={19} />
                {drawMode === 'polygon' && (
                  <FeatureGroup ref={featureGroupRef}>
                    <EditControl position="topright" onCreated={onCreated}
                      draw={{
                        polyline: false, rectangle: false, circle: false, marker: false, circlemarker: false,
                        polygon: { shapeOptions: polygonShapeOptions, icon: polygonVertexIcon, touchIcon: polygonVertexIcon, showArea: false, allowIntersection: false },
                      }}
                      edit={{ edit: false, remove: false }}
                    />
                    {drawnItems && <GeoJSON data={drawnItems.toGeoJSON()} style={polygonShapeOptions} />}
                  </FeatureGroup>
                )}
                <PanToLatLng target={geoFlyTarget} />
                <TwoPointMapHandler twoPoints={twoPoints} setTwoPoints={setTwoPoints} setFormData={setFormData} drawMode={drawMode} onPointMoved={handlePointMoved} />
                <FormMap markerPosition={markerPosition} setMarkerPosition={setMarkerPosition} setFormData={setFormData} drawMode={drawMode} onPointMoved={handlePointMoved} onBufferCleared={() => setDrawnItems(null)} />
              </MapContainer>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        <Box>
          <SectionLabel text="Event Date & Time" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            <TextField label="Date of Event" type="date" size="small" fullWidth sx={inputSx} name="date_flood" value={formData.date_flood} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            <TextField label="Time of Event" type="time" size="small" fullWidth sx={inputSx} name="time_flood" value={formData.time_flood} onChange={handleChange} InputLabelProps={{ shrink: true }} />
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <FormControl>
              <FieldLabel text="Is this date and time of occurrence or observation?" />
              <RadioGroup row value={formData.time_type} onChange={handleChange} name="time_type">
                <FormControlLabel value="Occurrence" control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Occurrence</Typography>} sx={{ mr: 3 }} />
                <FormControlLabel value="Observation" control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Observation</Typography>} />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        <Box>
          <SectionLabel text="Flood Characteristics" />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mb: 2.5 }}>
            <TextField label="Water Level" select size="small" fullWidth sx={inputSx} name="water_level" value={formData.water_level} onChange={handleChange}>
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a level</em></MenuItem>
              <MenuItem value="Ankle deep (< 0.3m)">Ankle deep (&lt; 0.3m)</MenuItem>
              <MenuItem value="Knee deep (0.3 - 0.6m)">Knee deep (0.3 - 0.6m)</MenuItem>
              <MenuItem value="Waist deep (0.6 - 1.2m)">Waist deep (0.6 - 1.2m)</MenuItem>
              <MenuItem value="Above waist (> 1.2m)">Above waist (&gt; 1.2m)</MenuItem>
              <MenuItem value="Don't know">Don't know</MenuItem>
            </TextField>

            <TextField label="How long has the flooding lasted?" select size="small" fullWidth sx={inputSx} name="flood_duration" value={formData.flood_duration} onChange={handleChange}>
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a duration</em></MenuItem>
              <MenuItem value="Less than 1 hour">Less than 1 hour</MenuItem>
              <MenuItem value="1 - 6 hours">1 - 6 hours</MenuItem>
              <MenuItem value="6 - 24 hours">6 - 24 hours</MenuItem>
              <MenuItem value="More than 24 hours">More than 24 hours</MenuItem>
              <MenuItem value="Still ongoing">Still ongoing</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <FormControl>
              <FieldLabel text="What has been affected/damaged? (check all that apply)" />
              <FormGroup row>
                {['Houses', 'Crops/Farmland', 'Roads/Bridges', 'Livestock', 'Public buildings'].map(v => (
                  <FormControlLabel key={v}
                    control={<Checkbox size="small" checked={formData.damage_type.includes(v)} onChange={handleChange} name="damage_type" value={v} sx={checkSx} />}
                    label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>{v}</Typography>}
                    sx={{ mr: 2 }}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            <TextField label="Apparent Cause of Flood" select size="small" fullWidth sx={inputSx} name="apparent_cause" value={formData.apparent_cause} onChange={handleChange}>
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a cause</em></MenuItem>
              <MenuItem value="Heavy rainfall">Heavy rainfall</MenuItem>
              <MenuItem value="River overflow">River overflow</MenuItem>
              <MenuItem value="Flash flood">Flash flood</MenuItem>
              <MenuItem value="Dam/reservoir release">Dam/reservoir release</MenuItem>
              <MenuItem value="Drainage blockage">Drainage blockage</MenuItem>
              <MenuItem value="Unknown">Unknown</MenuItem>
            </TextField>

            <TextField label="Were you directly affected by the reported flood?" select size="small" fullWidth sx={inputSx} name="affected_status" value={formData.affected_status} onChange={handleChange}>
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select an option</em></MenuItem>
              <MenuItem value="Yes, directly impacted">Yes, directly impacted</MenuItem>
              <MenuItem value="No, but observed it from a safe distance">No, but observed it from a safe distance</MenuItem>
              <MenuItem value="No, and observed it after it happened">No, and observed it after it happened</MenuItem>
            </TextField>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        <Box>
          <SectionLabel text="Losses & Damages" />
          <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1.5 }}>Approximate figures are fine — leave blank if unknown.</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 2.5 }}>
            <TextField label="People injured (approx.)" type="number" size="small" fullWidth sx={inputSx} name="people_injured_count" value={formData.people_injured_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Deaths reported (approx.)" type="number" size="small" fullWidth sx={inputSx} name="people_deaths_count" value={formData.people_deaths_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Houses affected (approx.)" type="number" size="small" fullWidth sx={inputSx} name="houses_affected_count" value={formData.houses_affected_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Livestock lost (approx.)" type="number" size="small" fullWidth sx={inputSx} name="livestock_lost_count" value={formData.livestock_lost_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Estimated monetary loss (USD)" type="number" size="small" fullWidth sx={inputSx} name="estimated_loss_amount" value={formData.estimated_loss_amount} onChange={handleChange} inputProps={{ min: 0, step: '0.01' }} />
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        <Box>
          <SectionLabel text="Description" />
          <FieldLabel text="Describe what happened" required />
          <TextField multiline rows={4} fullWidth required sx={inputSx} name="additional_info" value={formData.additional_info} onChange={handleChange} placeholder="Provide a description of this report" />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
          <Button type="button" variant="outlined" onClick={handleReset}
            sx={{ borderColor: '#d1d5db', color: '#374151', '&:hover': { borderColor: '#64748b', background: '#f8fafc' }, px: 4, py: 1, borderRadius: '8px', textTransform: 'none', fontSize: '14px', fontWeight: 600 }}>
            Reset
          </Button>
          <Button type="submit" variant="contained"
            sx={{ background: '#2563eb', '&:hover': { background: '#1d4ed8' }, px: 4, py: 1, borderRadius: '8px', textTransform: 'none', fontSize: '14px', fontWeight: 600, boxShadow: 'none' }}>
            Submit Report
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default FeltReport;
